import { z } from "zod";

export const healthSchema = z.object({
  status: z.string()
});

export const applicationSchema = z.object({
  id: z.string(),
  name: z.string(),
  labels: z.record(z.string(), z.unknown()),
  allowed_protocols: z.array(z.string()),
  enabled: z.boolean(),
  created_at: z.string(),
  updated_at: z.string()
});

export const applicationListSchema = z.object({
  object: z.literal("list"),
  data: z.array(applicationSchema)
});

const keyScopesSchema = z.object({
  endpoints: z.array(z.string()),
  models: z.array(z.string()),
  tools: z.array(z.string()),
  agents: z.array(z.string())
});

export const virtualKeySchema = z.object({
  id: z.string(),
  application_id: z.string(),
  prefix: z.string(),
  scopes: keyScopesSchema,
  expires_at: z.string().nullable(),
  revoked_at: z.string().nullable(),
  created_at: z.string()
});

export const keyListSchema = z.object({
  object: z.literal("list"),
  data: z.array(virtualKeySchema)
});

export const createdKeySchema = z.object({
  id: z.string(),
  application_id: z.string(),
  prefix: z.string(),
  key: z.string(),
  created_at: z.string()
});

export const secretSchema = z.object({
  id: z.string(),
  name: z.string(),
  key_version: z.number().int().nonnegative(),
  referenced_by: z.array(z.string()),
  created_at: z.string(),
  updated_at: z.string()
});

export const secretListSchema = z.object({
  object: z.literal("list"),
  data: z.array(secretSchema)
});
export const storedSecretSchema = z.object({
  name: z.string(),
  key_version: z.number().int().nonnegative(),
  created_at: z.string(),
  updated_at: z.string()
});

export const budgetSchema = z.object({
  soft_limit_microunits: z.number().int().nonnegative(),
  hard_limit_microunits: z.number().int().nonnegative(),
  spent_microunits: z.number().int().nonnegative(),
  reserved_microunits: z.number().int().nonnegative()
});

export const budgetPutSchema = z.object({
  application_id: z.string(),
  period_start: z.string(),
  period_end: z.string(),
  limit_microunits: z.number().int().nonnegative()
});

export const grantSchema = z.object({
  application_id: z.string(),
  resource_kind: z.string(),
  resource_id: z.string(),
  restrictions: z.record(z.string(), z.unknown()),
  created_at: z.string()
});

export const grantListSchema = z.object({
  object: z.literal("list"),
  data: z.array(grantSchema)
});

export const capabilitySchema = z.object({
  id: z.string(),
  resource_id: z.string(),
  resource_name: z.string(),
  namespace: z.string(),
  kind: z.string(),
  contract: z.unknown(),
  enabled: z.boolean()
});

export const capabilityListSchema = z.object({
  object: z.literal("list"),
  data: z.array(capabilitySchema)
});

export const fabricResourceSchema = z.object({
  id: z.string(),
  kind: z.enum(["channel", "mcp_server", "a2a_endpoint", "agent_profile", "policy_template"]),
  name: z.string(),
  active_revision: z.number().int().positive(),
  configuration: z.unknown(),
  secret_id: z.string().nullable(),
  capabilities: z.array(capabilitySchema),
  created_at: z.string(),
  updated_at: z.string()
});

export const fabricResourceListSchema = z.object({
  object: z.literal("list"),
  data: z.array(fabricResourceSchema)
});

export const modelRouteSchema = z.object({
  ordinal: z.number().int().nonnegative(),
  target_kind: z.enum(["capability", "agent_profile"]),
  target_id: z.string(),
  conversion_class: z.enum(["lossless", "compatible", "unsupported"]),
  route_config: z.record(z.string(), z.unknown())
});

export const virtualModelSchema = z.object({
  id: z.string(),
  public_name: z.string(),
  active_revision: z.number().int().positive(),
  routes: z.array(modelRouteSchema),
  created_at: z.string(),
  updated_at: z.string()
});

export const virtualModelListSchema = z.object({
  object: z.literal("list"),
  data: z.array(virtualModelSchema)
});

export const agentProfileSchema = z.object({
  id: z.string(),
  version: z.number().int().positive(),
  name: z.string(),
  instructions: z.string(),
  trust_domain: z.string(),
  model_grants: z.array(z.string()),
  tool_grants: z.array(z.string()),
  delegation_grants: z.array(z.string()),
  input_schema: z.unknown().nullable(),
  output_schema: z.unknown().nullable(),
  context_policy: z.object({
    include_parent_messages: z.boolean(),
    include_artifacts: z.boolean(),
    max_message_count: z.number().int().nonnegative(),
    allowed_labels: z.array(z.string())
  }),
  limits: z.object({
    max_depth: z.number().int().nonnegative(),
    max_fan_out: z.number().int().nonnegative(),
    max_steps: z.number().int().nonnegative(),
    max_concurrency: z.number().int().nonnegative(),
    max_repeated_fingerprint: z.number().int().nonnegative(),
    deadline_ms: z.number().int().nonnegative(),
    budget_microunits: z.number().int().nonnegative()
  })
});

export const agentProfileRecordSchema = z.object({
  resource_id: z.string(),
  version: z.number().int().positive(),
  profile: agentProfileSchema,
  created_at: z.string()
});

export const profileListSchema = z.object({
  object: z.literal("list"),
  data: z.array(agentProfileRecordSchema)
});

export const usageSchema = z.object({
  records: z.number().int().nonnegative(),
  input_tokens: z.number().int().nonnegative(),
  output_tokens: z.number().int().nonnegative(),
  images: z.number().int().nonnegative(),
  cost_microunits: z.number().int().nonnegative()
});

export const auditSchema = z.object({
  id: z.string(),
  application_id: z.string().nullable(),
  actor_type: z.string(),
  actor_id: z.string(),
  action: z.string(),
  resource_type: z.string(),
  resource_id: z.string(),
  metadata: z.record(z.string(), z.unknown()),
  previous_hash: z.string().nullable(),
  event_hash: z.string(),
  created_at: z.string()
});

export const auditListSchema = z.object({
  object: z.literal("list"),
  data: z.array(auditSchema)
});

export const responseSchema = z.object({
  id: z.string(),
  application_id: z.string(),
  northbound_protocol: z.string(),
  public_model: z.string(),
  status: z.string(),
  execution_path: z.string(),
  background: z.boolean(),
  key_identity: z.string().nullable(),
  provider_response_id: z.string().nullable(),
  provider_protocol: z.string().nullable(),
  provider_cost_microunits: z.number().int().nullable(),
  route_decision: z.unknown().nullable(),
  created_at: z.string(),
  updated_at: z.string()
});

export const responseListSchema = z.object({
  object: z.literal("list"),
  data: z.array(responseSchema)
});

export const traceSchema = z.object({
  id: z.string(),
  application_id: z.string(),
  response_id: z.string(),
  metadata: z.unknown(),
  started_at: z.string(),
  ended_at: z.string().nullable()
});

export const responseDetailSchema = z.object({
  response: responseSchema,
  trace: traceSchema.nullable(),
  execution_graph: z.unknown().nullable()
});

export const taskStateSchema = z.enum([
  "queued",
  "in_progress",
  "waiting",
  "completed",
  "failed",
  "canceled",
  "needs_attention"
]);

export const taskNodeCountsSchema = z.object({
  pending: z.number().int(),
  running: z.number().int(),
  waiting_approval: z.number().int(),
  completed: z.number().int(),
  skipped: z.number().int(),
  failed: z.number().int(),
  canceled: z.number().int(),
  needs_attention: z.number().int()
});

export const taskSummarySchema = z.object({
  id: z.string(),
  application_id: z.string(),
  northbound_protocol: z.string(),
  public_model: z.string(),
  status: taskStateSchema,
  background: z.boolean(),
  nodes: taskNodeCountsSchema,
  created_at: z.string(),
  updated_at: z.string()
});

export const taskListSchema = z.object({
  object: z.literal("list"),
  data: z.array(taskSummarySchema)
});

export const graphNodeSchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: z.string(),
  depth: z.number().int().nonnegative(),
  capability: z.string().nullable(),
  side_effect: z.string(),
  configuration: z.unknown(),
  status: z.string(),
  attempt_count: z.number().int().nonnegative(),
  idempotency_key: z.string().nullable(),
  side_effect_confirmed: z.boolean(),
  output: z.unknown().nullable(),
  error_code: z.string().nullable(),
  error_message: z.string().nullable(),
  updated_at: z.string()
});

export const graphEdgeSchema = z.object({
  from: z.string(),
  to: z.string(),
  condition: z.string().nullable(),
  selected: z.boolean().nullable()
});

export const taskGraphSchema = z.object({
  id: z.string(),
  nodes: z.array(graphNodeSchema),
  edges: z.array(graphEdgeSchema),
  created_at: z.string()
});

export const approvalSchema = z.object({
  id: z.string(),
  response_id: z.string(),
  application_id: z.string(),
  node_id: z.string(),
  node_name: z.string(),
  node_kind: z.string(),
  public_model: z.string(),
  status: z.string(),
  reason: z.string().nullable(),
  requested_at: z.string(),
  decided_at: z.string().nullable()
});

export const approvalListSchema = z.object({
  object: z.literal("list"),
  data: z.array(approvalSchema)
});

export const artifactSchema = z.object({
  id: z.string(),
  response_id: z.string(),
  node_id: z.string(),
  media_type: z.string(),
  labels: z.record(z.string(), z.unknown()),
  retention_state: z.string(),
  size_bytes: z.number().int().nonnegative(),
  sha256: z.string(),
  storage_key: z.string(),
  created_at: z.string()
});

export const artifactListSchema = z.object({
  object: z.literal("list"),
  data: z.array(artifactSchema)
});

export const eventSchema = z.object({
  sequence: z.number().int().positive(),
  event_type: z.string(),
  event: z.unknown(),
  created_at: z.string()
});

export const eventListSchema = z.object({
  object: z.literal("list"),
  data: z.array(eventSchema)
});

export const attentionErrorSchema = z.object({
  node_id: z.string(),
  node_name: z.string(),
  error_code: z.string(),
  error_message: z.string()
});

export const attentionSchema = z.object({
  id: z.string(),
  application_id: z.string(),
  public_model: z.string(),
  attention_nodes: z.number().int(),
  errors: z.array(attentionErrorSchema),
  updated_at: z.string()
});

export const attentionListSchema = z.object({
  object: z.literal("list"),
  data: z.array(attentionSchema)
});

export const taskDetailSchema = z.object({
  id: z.string(),
  application_id: z.string(),
  northbound_protocol: z.string(),
  public_model: z.string(),
  execution_path: z.string(),
  status: taskStateSchema,
  background: z.boolean(),
  route_decision: z.unknown().nullable(),
  graph: taskGraphSchema.nullable(),
  approvals: z.array(approvalSchema),
  artifacts: z.array(artifactSchema),
  created_at: z.string(),
  updated_at: z.string()
});

export const taskDetailEnvelopeSchema = z.object({
  task: taskDetailSchema
});

export const taskCreatedSchema = z.object({
  task: taskDetailSchema
});

export const taskCanceledSchema = z.object({
  id: z.string(),
  public_model: z.string(),
  status: z.string(),
  created_at: z.string()
});

export const approvalDecisionSchema = z.object({
  id: z.string(),
  response_id: z.string(),
  status: z.string()
});

export const runtimeHealthSchema = z.object({
  durable_responses: z.record(z.string(), z.number().int()),
  pending_approvals: z.number().int(),
  running_nodes: z.number().int(),
  active_leases: z.number().int(),
  undelivered_outbox: z.number().int(),
  artifact_objects: z.number().int(),
  artifact_bytes: z.number().int(),
  oldest_lease_expiry: z.string().nullable()
});

export const affectedGrantSchema = z.object({
  application_id: z.string(),
  application_name: z.string(),
  resource_kind: z.string(),
  resource_id: z.string(),
  restrictions: z.record(z.string(), z.unknown())
});

export const affectedModelSchema = z.object({
  id: z.string(),
  public_name: z.string(),
  active_revision: z.number().int().positive(),
  route_ordinals: z.array(z.number().int())
});

export const resourceImpactSchema = z.object({
  id: z.string(),
  kind: z.string(),
  name: z.string(),
  active_revision: z.number().int().positive(),
  capability_count: z.number().int(),
  response_count: z.number().int(),
  affected_grants: z.array(affectedGrantSchema),
  affected_models: z.array(affectedModelSchema)
});

export const connectionTestSchema = z.object({
  id: z.string(),
  kind: z.string(),
  endpoint: z.string(),
  ok: z.boolean(),
  status: z.number().int(),
  latency_ms: z.number().int(),
  discovered_models: z.number().int()
});

export const auditVerificationSchema = z.null();

export type Application = z.infer<typeof applicationSchema>;
export type VirtualKey = z.infer<typeof virtualKeySchema>;
export type CreatedKey = z.infer<typeof createdKeySchema>;
export type Secret = z.infer<typeof secretSchema>;
export type StoredSecret = z.infer<typeof storedSecretSchema>;
export type Budget = z.infer<typeof budgetSchema>;
export type Grant = z.infer<typeof grantSchema>;
export type Capability = z.infer<typeof capabilitySchema>;
export type FabricResource = z.infer<typeof fabricResourceSchema>;
export type VirtualModel = z.infer<typeof virtualModelSchema>;
export type ModelRoute = z.infer<typeof modelRouteSchema>;
export type AgentProfile = z.infer<typeof agentProfileSchema>;
export type AgentProfileRecord = z.infer<typeof agentProfileRecordSchema>;
export type Usage = z.infer<typeof usageSchema>;
export type Audit = z.infer<typeof auditSchema>;
export type ResponseSearch = z.infer<typeof responseSchema>;
export type Trace = z.infer<typeof traceSchema>;
export type TaskSummary = z.infer<typeof taskSummarySchema>;
export type TaskDetail = z.infer<typeof taskDetailSchema>;
export type TaskGraph = z.infer<typeof taskGraphSchema>;
export type TaskNode = z.infer<typeof graphNodeSchema>;
export type TaskEdge = z.infer<typeof graphEdgeSchema>;
export type Approval = z.infer<typeof approvalSchema>;
export type Artifact = z.infer<typeof artifactSchema>;
export type Event = z.infer<typeof eventSchema>;
export type Attention = z.infer<typeof attentionSchema>;
export type RuntimeHealth = z.infer<typeof runtimeHealthSchema>;
export type ResourceImpact = z.infer<typeof resourceImpactSchema>;
export type ConnectionTest = z.infer<typeof connectionTestSchema>;

// --- Credential pools and pricing catalog -----------------------------------

export const credentialMemberSchema = z.object({
  label: z.string(),
  enabled: z.boolean(),
  priority: z.number().int().nonnegative(),
  weight: z.number().int().nonnegative(),
  consecutive_failures: z.number().int().nonnegative(),
  cooldown_until: z.string().nullable(),
  last_error: z.string().nullable(),
  latency_ms: z.number().int().nonnegative(),
  last_used_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string()
});

export const credentialMemberListSchema = z.object({
  object: z.literal("list"),
  data: z.array(credentialMemberSchema)
});

export const credentialProbeSchema = z.object({
  label: z.string(),
  ok: z.boolean(),
  status: z.number().int().nonnegative(),
  latency_ms: z.number().int().nonnegative()
});

export const pricingSchema = z.object({
  public_model: z.string(),
  input_price_microunits_per_mtok: z.number().int().nonnegative(),
  output_price_microunits_per_mtok: z.number().int().nonnegative(),
  updated_at: z.string()
});

export const pricingListSchema = z.object({
  object: z.literal("list"),
  data: z.array(pricingSchema)
});

export const costEstimateSchema = z.object({
  model: z.string(),
  input_tokens: z.number().int().nonnegative(),
  output_tokens: z.number().int().nonnegative(),
  cost_microunits: z.number().int().nonnegative()
});

export type CredentialMember = z.infer<typeof credentialMemberSchema>;
export type Pricing = z.infer<typeof pricingSchema>;
export type CostEstimate = z.infer<typeof costEstimateSchema>;

export function normalizeState(status: string): string {
  switch (status) {
    case "in_progress":
      return "running";
    case "waiting":
      return "waiting_approval";
    case "queued":
      return "pending";
    default:
      return status;
  }
}
